import React from 'react';
import PropTypes from 'prop-types';
import ReviewQuoteProducts from './quote-products/ReviewQuoteProducts';
import ReviewQuoteInstallation from './quote-installation/ReviewQuoteInstallation';
import ReviewQuoteExpeditedShipping from './quote-details/ReviewQuoteExpeditedShipping';
import ReviewQuoteDetailsContainer from './quote-details/ReviewQuoteDetailsContainer';
import ReviewQuoteCustomLineItems from './quote-custom-line-items/ReviewQuoteCustomLineItems';
import ReviewQuotePrice from './ReviewQuotePrice';
import { DealerGroup } from '../../dealer-groups/dealerGroup';
import { DealerBranch } from '../../dealer-branches/dealerBranch';
import { DealerUserSearchResult } from '../../dealer-users/dealerUserService';
import { ProductConfiguration } from '../../products/productConfiguration';
import { Quote } from '../quote';
import { couplerDecision } from '../../quotes/otherQuoteEntities';
import { QuoteProductConfiguration } from '../../quotes/quoteProductConfiguration';
import { User } from '../../users/user';
import { Spinner } from '@ami/react/core';
import { makeValidable } from '@ami/react/core';
import {
  getDefaultPriceMode,
  getMsrpColumnClassNames,
  getDNetColumnClassNames,
} from '../../quotes/quotePriceMode';
import _ from 'lodash';
import { callbackOnWidthChange } from '@ami/react/core';
import { NotificationSettingsComponent } from '../../NotificationSettingsComponent';
import { appConfig } from '@ami/react/core';
import { Team } from '../../teams/team';
import { ProductOptionSearchItem } from '../../products/productOption';
import { getQuoteProductLeadTime } from '../../quotes/lead-times/leadTimeCalculator';
import LeadTimeWarnings from '../../quotes/LeadTimeWarnings';
import QuoteSharedDocumentsListViewComponent from '../../quotes/view/quote-documents/QuoteSharedDocumentsListViewComponent';
import ReviewQuoteSpinnerComponent from './ReviewQuoteSpinnerComponent';
import { QuoteOptInDiscountOutput } from '../entities/QuoteOptInDiscountOutput';
import ReviewQuoteOptInDiscountsList from './quote-details/ReviewQuoteOptInDiscountsList';
import QuoteReviewWarning from '../../quotes/QuoteReviewWarning';
import { CancelConfirmationModal } from '@ami/react/core';

const ValidableReviewQuoteInstallation = makeValidable(ReviewQuoteInstallation);

export default class ReviewQuoteComponent extends React.Component {
  constructor(props) {
    super(props);

    this.validableRef = React.createRef();

    this.handleOnSaveClicked = this.handleOnSaveClicked.bind(this);
    this.defaultPriceModeCallback = this.defaultPriceModeCallback.bind(this);
    this.toggleDiscount = this.toggleDiscount.bind(this);

    const { showReviewAlert } = this.props;
    this.state = {
      showValidations: false,
      selectedPriceMode: getDefaultPriceMode(),
      width: window.innerWidth,
      showReviewAlert: showReviewAlert,
      copiedQuote: this.props.quote,
      showNotesConfirmModal: false,
      checkToShowReviewAlert: !showReviewAlert
    };
  }

  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    dealerGroup: PropTypes.instanceOf(DealerGroup).isRequired,
    dealerBranch: PropTypes.instanceOf(DealerBranch).isRequired,
    dealerGroupUsers: PropTypes.arrayOf(
      PropTypes.instanceOf(DealerUserSearchResult)
    ).isRequired,
    dealerUsers: PropTypes.arrayOf(PropTypes.instanceOf(DealerUserSearchResult))
      .isRequired,
    amiUsers: PropTypes.arrayOf(PropTypes.instanceOf(User)).isRequired,
    dealerBranchSalesTeam: PropTypes.instanceOf(Team),
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration)
      .isRequired,
    onAddCoupler: PropTypes.func.isRequired,
    onAddProduct: PropTypes.func.isRequired,
    onEditProduct: PropTypes.func.isRequired,
    onQuoteUpdated: PropTypes.func.isRequired,
    onQuoteAccepted: PropTypes.func.isRequired,
    onRemoveProduct: PropTypes.func.isRequired,
    onRemoveCustomLineItem: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    onCancelEdit: PropTypes.func.isRequired,
    onNewDealerUserAssigned: PropTypes.func.isRequired,
    productOptions: PropTypes.arrayOf(
      PropTypes.instanceOf(ProductOptionSearchItem)
    ).isRequired,
    submitting: PropTypes.bool.isRequired,
    sharedDocuments: PropTypes.array.isRequired,
    onDownload: PropTypes.func.isRequired,
    documentIdDownloading: PropTypes.string,
    onloadSharedDocuments: PropTypes.func.isRequired,
    optInDiscounts: PropTypes.arrayOf(PropTypes.instanceOf(QuoteOptInDiscountOutput))
      .isRequired,
    showPricingSpinner: PropTypes.bool.isRequired,
    showReviewAlert: PropTypes.bool.isRequired
  };

  updateProductQuantity = (quoteProductId, quantity) => {
    this._updateQuote(
      (copy) =>
        copy.updateProductQuantity(
          quoteProductId,
          quantity
        ),
      true
    );
  };

  updateCustomLineItemQuantity = (quoteCustomLineItemId, quantity) => {
    this._updateQuote(
      (copy) =>
        copy.updateCustomLineItemQuantity(
          quoteCustomLineItemId,
          quantity
        ),
      true
    );
  };

  toggleDiscount = (checked, discountId, documentationFileKey, documentationFileName) => {
    this._updateQuote(
      (copy) => {
        if (checked)
          copy.optInDiscount(discountId, documentationFileKey, documentationFileName);
        else
          copy.optOutDiscount(discountId);
      },
      true
    );
  };

  onUseLatestDiscounts = () => {
    this._updateQuote(
      (copy) =>
        copy.setUseLatestDiscounts(),
      true
    );
  };

  addProductCustomization = (quoteProductId, customization) => {
    this._updateQuote(
      (copy) =>
        copy.addProductCustomization(
          quoteProductId,
          customization
        ),
      true
    );
  };

  removeProductCustomization = (quoteProductId, customizationIdx) => {
    this._updateQuote(
      (copy) =>
        copy.removeProductCustomization(
          quoteProductId,
          customizationIdx
        ),
      true
    );
  };

  setProductOverrideDiscount = (quoteProductId, overrideDiscount) => {
    this._updateQuote(
      (copy) =>
        copy.setProductOverrideDiscount(
          quoteProductId,
          overrideDiscount
        ),
      true
    );
  };

  removeProductOverrideDiscount = (quoteProductId) => {
    this._updateQuote(
      (copy) =>
        copy.removeProductOverrideDiscount(
          quoteProductId
        ),
      true
    );
  };

  updateProduct = (quoteProduct) => {
    this._updateQuote(
      (copy) =>
        copy.updateProduct(quoteProduct),
      true
    );
  };

  editCustomLineItem = (item) => {
    this._updateQuote((copy) => copy.updateCustomLineItem(item), true);
  };

  addCustomLineItem = (item) => {
    this._updateQuote((copy) => copy.addCustomLineItem(item), true);
  };

  setCustomLineItemOverrideDiscount = (customLineItemId, overrideDiscount) => {
    this._updateQuote(
      (copy) =>
        copy.setCustomLineItemOverrideDiscount(
          customLineItemId,
          overrideDiscount
        ),
      true
    );
  };

  removeCustomLineItemOverrideDiscount = (customLineItemId) => {
    this._updateQuote(
      (copy) =>
        copy.removeCustomLineItemOverrideDiscount(
          customLineItemId
        ),
      true
    );
  };

  updateInstallation = (updatedInstallation) => {
    this._updateQuote((copy) => (copy.installation = updatedInstallation));
  };

  updateNotificationSettings = (updatedNotificationSettings) => {
    this._updateQuote(
      (copy) => (copy.notificationSettings = updatedNotificationSettings)
    );
  };

  updateOverallDiscounts = (overallDiscounts) => {
    this._updateQuote((copy) => (copy.overallDiscounts = overallDiscounts));
  };

  updateLeadTimeOverride = (quoteProduct) => {
    quoteProduct.leadTime = getQuoteProductLeadTime(
      quoteProduct,
      this.props.quoteProductConfiguration
    );
    this._updateQuote((copy) =>
      copy.updateProduct(quoteProduct)
    );
  };

  _updateQuote = async (updateCallBack, updateQuotePricing) => {
    const copy = this.props.quote.clone();
    updateCallBack(copy);
    return this.props.onQuoteUpdated(copy, updateQuotePricing);
  };

  _onQuoteUpdated = async (updatedQuote) => {
    const hasManufacturingInstructionsReview = this.hasManufacturingInstructionsReview(updatedQuote);
    this.setState({ showReviewAlert: hasManufacturingInstructionsReview });

    updatedQuote.maskPricingAndLeadTimes = hasManufacturingInstructionsReview;
    return this.props.onQuoteUpdated(updatedQuote);
  };

  hasManufacturingInstructionsReview = (quote) => {
    const productsWithManufacturingInstructions = quote?.products.filter(x => x.requiresReview && !x.isReviewed) || [];
    return productsWithManufacturingInstructions.length > 0;
  }

  handleOnSaveClicked = () => {
    this.setState({ showValidations: true }, () => {
      if (this.validableRef.current.isValid()) {
        this.props.onQuoteAccepted();
      }
    });
  };

  componentDidMount() {
    window.addEventListener(
      'resize',
      _.debounce(this.setDefaultPriceMode, 250)
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'resize',
      _.debounce(this.setDefaultPriceMode, 250)
    );
  }

  defaultPriceModeCallback = (newWidth) => {
    this.setState({
      width: newWidth,
      selectedPriceMode: getDefaultPriceMode(),
    });
  };

  setDefaultPriceMode = () => {
    callbackOnWidthChange(this.state.width, this.defaultPriceModeCallback);
  };

  updatePriceMode = (priceModeId) => {
    this.setState({
      selectedPriceMode: priceModeId,
    });
  };

  onCheckToShowReviewAlert = () => {
    const { checkToShowReviewAlert } = this.state;

    if (!checkToShowReviewAlert)
      return;

    const { quote } = this.props;
    const showReviewAlert = this.hasManufacturingInstructionsReview(quote);

    this.setState({ showReviewAlert, checkToShowReviewAlert: false });
  }

  render() {
    const {
      quote,
      productConfiguration,
      quoteProductConfiguration,
      onAddCoupler,
      onAddProduct,
      onEditProduct,
      onRemoveProduct,
      isEditMode,
      onRemoveCustomLineItem,
      onQuoteUpdated,
      dealerGroup,
      dealerBranch,
      dealerGroupUsers,
      dealerUsers,
      amiUsers,
      dealerBranchSalesTeam,
      onCancelEdit,
      onNewDealerUserAssigned,
      productOptions,
      submitting,
      sharedDocuments,
      onDownload,
      documentIdDownloading,
      onloadSharedDocuments,
      optInDiscounts,
      showPricingSpinner
    } = this.props;

    const { selectedPriceMode, showValidations, showReviewAlert, showNotesConfirmModal } = this.state;
    const msrpClassNames = getMsrpColumnClassNames(selectedPriceMode);
    const dNetClassNames = getDNetColumnClassNames(selectedPriceMode);

    const machineMissing = !quote.step1.model || quote.step1.model === '';
    const couplerMissing = !!quote.attachmentPoints.find(
      (x) => x.couplerDecision === couplerDecision.notSpecified
    );

    this.onCheckToShowReviewAlert();

    return (
      <form className="container">
        <ReviewQuoteSpinnerComponent show={showPricingSpinner} />
        <div className="col-md-6">
          <h2 className="page-title">
            {isEditMode ? `Edit Quote ${quote.number}` : 'Review Quote'}
          </h2>
        </div>
        <QuoteReviewWarning show={showReviewAlert} />
        <LeadTimeWarnings
          machineMissing={machineMissing}
          couplerMissing={couplerMissing}
          fullWidth={true}
        />
        <ReviewQuoteDetailsContainer
          quote={quote}
          dealerBranch={dealerBranch}
          dealerUsers={dealerUsers}
          amiUsers={amiUsers}
          onUpdate={this._onQuoteUpdated}
          onNewDealerUserAssigned={onNewDealerUserAssigned}
          productConfiguration={productConfiguration}
          quoteProductConfiguration={quoteProductConfiguration}
          onAddCoupler={onAddCoupler}
          isEditMode={isEditMode}
          onloadSharedDocuments={onloadSharedDocuments}
        />
        <ReviewQuoteProducts
          quote={quote}
          quoteProductConfiguration={quoteProductConfiguration}
          onQuantityUpdated={this.updateProductQuantity}
          onLeadTimeOverrideUpdated={this.updateLeadTimeOverride}
          onAddProduct={onAddProduct}
          onEditProduct={onEditProduct}
          onRemoveProduct={onRemoveProduct}
          productConfiguration={productConfiguration}
          selectedPriceMode={selectedPriceMode}
          updatePriceMode={this.updatePriceMode}
          onAddProductCustomization={this.addProductCustomization}
          onUpdateProduct={this.updateProduct}
          onRemoveProductCustomization={this.removeProductCustomization}
          onAddOverrideDiscount={this.setProductOverrideDiscount}
          onEditOverrideDiscount={this.setProductOverrideDiscount}
          onRemoveOverrideDiscount={this.removeProductOverrideDiscount}
          msrpClassNames={msrpClassNames}
          dNetClassNames={dNetClassNames}
          productOptions={productOptions}
          isEditMode={isEditMode}
        />
        <ReviewQuoteCustomLineItems
          customLineItems={quote.customLineItems}
          onEdited={this.editCustomLineItem}
          onRemove={onRemoveCustomLineItem}
          onAdded={this.addCustomLineItem}
          selectedPriceMode={selectedPriceMode}
          msrpClassNames={msrpClassNames}
          dNetClassNames={dNetClassNames}
          onQuantityUpdated={this.updateCustomLineItemQuantity}
          onAddOverrideDiscount={this.setCustomLineItemOverrideDiscount}
          onEditOverrideDiscount={this.setCustomLineItemOverrideDiscount}
          onRemoveOverrideDiscount={this.removeCustomLineItemOverrideDiscount}
          isoCurrencySymbol={quoteProductConfiguration.isoCurrencySymbol}
        />
        <ReviewQuoteExpeditedShipping quote={quote} onUpdate={onQuoteUpdated} />
        <ValidableReviewQuoteInstallation
          ref={this.validableRef}
          showValidations={showValidations}
          quote={quote}
          onInstallationUpdate={this.updateInstallation}
          selectedPriceMode={selectedPriceMode}
          msrpClassNames={msrpClassNames}
          dNetClassNames={dNetClassNames}
          isoCurrencySymbol={quoteProductConfiguration.isoCurrencySymbol}
        />
        {!dealerBranch.disableAdditionalDiscounts &&
          <ReviewQuoteOptInDiscountsList
            quote={quote}
            optInDiscounts={optInDiscounts}
            onToggleDiscount={this.toggleDiscount}
            useLatestDiscounts={quote.useLatestDiscounts}
            onSetUseLatestDiscounts={this.onUseLatestDiscounts}
          />
        }
        {dealerBranch.disableAdditionalDiscounts && appConfig.isManagementApp &&
          <div className="card mb-4 box-shadow municipal-tender">
            <div className="card-body">
              <h6 className="card-title text-center">Discounts</h6>
              <div className="row justify-content-center">
                Additional discounts are not enabled for this dealer branch
              </div>
            </div>
          </div>
        }
        <div className="row row-cols-1 row-cols-md-2">
          <div className="col d-flex">
            <QuoteSharedDocumentsListViewComponent
              documents={sharedDocuments}
              onDownload={onDownload}
              documentIdDownloading={documentIdDownloading}
            />
          </div>
          <div className="col d-flex">
            <div
              className="card mb-4 box-shadow flex-fill"
              style={{ backgroundColor: 'transparent', border: 'none' }}
            >
              <div className="card-body">
                <div className="row">
                  <div className="col">
                    <ReviewQuotePrice
                      quote={quote}
                      selectedPriceMode={selectedPriceMode}
                      isoCurrencySymbol={quoteProductConfiguration.isoCurrencySymbol}
                      onOverallDiscountsUpdate={this.updateOverallDiscounts}
                      onUpdate={onQuoteUpdated}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {appConfig.isManagementApp && !quote.dealerUserId && (
          <div className="card-deck">
            <div className="card mb-4 box-shadow">
              <div className="card-body">
                <h6 className="card-title text-center">
                  Notification Settings
                </h6>
                <div className="text-center">
                  Please select a Dealer Contact before managing notification settings
                </div>
              </div>
            </div>
          </div>
        )}
        {appConfig.isManagementApp && quote.dealerUserId && (
          <NotificationSettingsComponent
            isEmailCommunicationEnabled={
              dealerGroup.isEmailCommunicationEnabled
            }
            dealerGroupId={dealerGroup.id}
            dealerGroupUsers={dealerGroupUsers}
            dealerUser={dealerUsers.find((x) => x.id === quote.dealerUserId)}
            amiUsers={amiUsers.filter(
              (x) =>
                x.id === quote.salesPersonId ||
                x.id === quote.territoryManagerId ||
                dealerBranchSalesTeam.amiUserIds.includes(x.id) ||
                (isEditMode && quote.subscribedAmiUserIds.includes(x.id))
            )}
            isEditMode={isEditMode}
            notificationSettings={quote.notificationSettings}
            onNotificationSettingsUpdate={this.updateNotificationSettings}
            entityName="Quote"
            defaultDealerContactIds={dealerBranch.defaultDealerContactIds}
            enabledNotificationSchedule={false}
          />
        )}
        <div className="row mt-2 mb-4">
          <div className="col text-end">
            <div className="clearfix">
              <div className="pull-right">
                <button
                  type="button"
                  className="btn btn-secondary btn-lg ms-3 mb-2"
                  onClick={onCancelEdit}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-lg ms-3 mb-2"
                  onClick={() => quote.notes.trim() ? this.setState({ showNotesConfirmModal: true }) : this.handleOnSaveClicked()}
                  disabled={submitting}
                >
                  Save <Spinner show={submitting} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <CancelConfirmationModal
          title={`Confirm Submitted Notes`}
          text={`A Sales Coordinator will need to confirm that the notes on this quote do not affect pricing & lead times. This will not prevent PO submission. However, it could delay creating an order for this quote. \n\n Do you want to continue?`}
          show={showNotesConfirmModal}
          onShowUpdated={() => { }}
          onYes={() => this.handleOnSaveClicked()}
          onNo={() => this.setState({ showNotesConfirmModal: false })}
          submitting={submitting}
        />
      </form>
    );
  }
}
